import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Shield, Key, AlertTriangle, CheckCircle, Search, User } from 'lucide-react';

/**
 * UserKeyRecovery — Admin panel for E2EE key escrow management.
 * Shows which users have E2EE initialized and allows admins to see recovery status.
 */
export function UserKeyRecovery() {
    const { users, currentUser, hasPermission } = useApp();
    const [search, setSearch] = useState('');

    // Only admins can see this
    if (!hasPermission('admin:full') && !hasPermission('super:admin')) {
        return null;
    }

    const filteredUsers = users.filter(u =>
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase())
    );

    const e2eeUsers = filteredUsers.filter(u => (u as any).e2ee_initialized);
    const noE2eeUsers = filteredUsers.filter(u => !(u as any).e2ee_initialized && u.invite_status === 'accepted');

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Header */}
            <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-color)',
                overflow: 'hidden'
            }}>
                <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Shield size={16} style={{ color: 'var(--accent)' }} />
                        <span style={{ fontWeight: 600, fontSize: 14 }}>E2EE Key Escrow Status</span>
                    </div>
                    <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
                        Monitor which users have end-to-end encryption initialized. Recovery keys are sent to the configured escrow email on first setup.
                    </p>
                </div>

                <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-color)' }}>
                    <div style={{ position: 'relative', maxWidth: 320 }}>
                        <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            className="input"
                            placeholder="Search users..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ paddingLeft: 32, width: '100%' }}
                        />
                    </div>
                </div>

                {/* Stats */}
                <div style={{ padding: '12px 20px', display: 'flex', gap: 24, borderBottom: '1px solid var(--border-color)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CheckCircle size={14} style={{ color: '#16a34a' }} />
                        <span style={{ fontSize: 13 }}>
                            <strong>{e2eeUsers.length}</strong> <span style={{ color: 'var(--text-muted)' }}>E2EE active</span>
                        </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <AlertTriangle size={14} style={{ color: '#eab308' }} />
                        <span style={{ fontSize: 13 }}>
                            <strong>{noE2eeUsers.length}</strong> <span style={{ color: 'var(--text-muted)' }}>not initialized</span>
                        </span>
                    </div>
                </div>

                {/* User list */}
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {filteredUsers.filter(u => u.invite_status === 'accepted').map(user => {
                        const hasE2EE = !!(user as any).e2ee_initialized;
                        const wasReset = !!(user as any).e2ee_reset_at;
                        const isMe = user.id === currentUser?.id;

                        return (
                            <div
                                key={user.id}
                                style={{
                                    padding: '12px 20px',
                                    borderBottom: '1px solid var(--border-color)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12
                                }}
                            >
                                {/* Avatar */}
                                <div style={{
                                    width: 32, height: 32, borderRadius: '50%',
                                    background: 'var(--bg-tertiary)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 13, fontWeight: 600, flexShrink: 0
                                }}>
                                    {user.avatar
                                        ? <img src={user.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                        : user.name.charAt(0).toUpperCase()
                                    }
                                </div>

                                {/* Info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                                        {user.name} {isMe && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(you)</span>}
                                    </div>
                                    <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {user.email}
                                    </div>
                                </div>

                                {/* E2EE Status */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                    {hasE2EE ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(22, 163, 74, 0.1)', border: '1px solid rgba(22, 163, 74, 0.3)', borderRadius: 6, padding: '3px 8px' }}>
                                            <Key size={12} style={{ color: '#16a34a' }} />
                                            <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 500 }}>E2EE Active</span>
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(234, 179, 8, 0.08)', border: '1px solid rgba(234, 179, 8, 0.3)', borderRadius: 6, padding: '3px 8px' }}>
                                            <AlertTriangle size={12} style={{ color: '#eab308' }} />
                                            <span style={{ fontSize: 11, color: '#eab308', fontWeight: 500 }}>Not Set Up</span>
                                        </div>
                                    )}
                                    {wasReset && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 6, padding: '3px 8px' }}>
                                            <AlertTriangle size={12} style={{ color: '#ef4444' }} />
                                            <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 500 }}>Key Reset</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {filteredUsers.filter(u => u.invite_status === 'accepted').length === 0 && (
                        <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                            <User size={24} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
                            No users found
                        </div>
                    )}
                </div>
            </div>

            {/* Recovery Instructions */}
            <div style={{
                background: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border-color)',
                padding: '16px 20px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <Key size={14} style={{ color: 'var(--accent)' }} />
                    <span style={{ fontWeight: 600, fontSize: 13 }}>How to Recover a User's Messages</span>
                </div>
                <ol style={{ fontSize: 12, color: 'var(--text-secondary)', paddingLeft: 16, lineHeight: 1.8, margin: 0 }}>
                    <li>Locate the recovery key email sent to your escrow address when the user first set up E2EE.</li>
                    <li>The recovery key can decrypt the user's <code>enc_private_key_escrow</code> blob stored in the database.</li>
                    <li>Use the recovery key to decrypt the escrow blob and obtain the user's private key.</li>
                    <li>Use the private key to decrypt the user's encrypted messages.</li>
                    <li>If the user reset their password, use the <code>enc_private_key_escrow_archived</code> blob instead.</li>
                </ol>
                <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
                    ⚠️ Recovery keys are never stored on this server. They are only sent to the configured escrow email address.
                </p>
            </div>
        </div>
    );
}
